import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './CalendarView.css';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import { getMaintenanceTypeLabel as getTypeLabel, getMaintenanceTypeIcon as getTypeIcon } from '../utils/maintenanceUtils';
import { API_ENDPOINTS, API_BASE_URL } from '../../../config/apiConfig';

const FILTER_TYPES = [
  { type: 'ppm', label: 'PPM' },
  { type: 'calibration', label: 'CAL' },
  { type: 'compliance', label: 'COM' },
];

const TYPE_DOT_COLOR = {
  ppm: '#3b82f6',
  calibration: '#f59e0b',
  compliance: '#16a34a',
};

const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const scheduleDateKey = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toLocalDateStr(value);
  }
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

function CalendarView({
  token,
  onNavigateToDevices,
  onViewDevice,
  departmentId,
  locationId,
  variant = 'default',
}) {
  const isCompact = variant === 'compact';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(new Set(['ppm', 'calibration', 'compliance']));
  const [selectedDateDetails, setSelectedDateDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const toggleFilter = (filterType) => {
    const newFilters = new Set(selectedFilters);
    if (newFilters.has(filterType)) {
      newFilters.delete(filterType);
    } else {
      newFilters.add(filterType);
    }
    setSelectedFilters(newFilters);
  };

  const fetchScheduleData = async (year, month) => {
    setLoading(true);
    setError('');

    try {
      if (!token) {
        console.error('Token is missing!');
        setError('Authentication token is missing. Please log in again.');
        setLoading(false);
        return;
      }

      const startDateStr = toLocalDateStr(new Date(year, month, 1));
      const endDateStr = toLocalDateStr(new Date(year, month + 1, 0));

      let queryParams = `start_date=${startDateStr}&end_date=${endDateStr}`;
      if (departmentId) {
        queryParams += `&department_id=${departmentId}`;
      }
      if (locationId) {
        queryParams += `&location_id=${locationId}`;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.SCHEDULE.LIST}?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data.schedule || !Array.isArray(response.data.schedule)) {
        console.error('Invalid data structure:', response.data);
        setError('Invalid data structure from server.');
        setScheduleData([]);
        setLoading(false);
        return;
      }

      setScheduleData(response.data.schedule);
      setError('');
    } catch (err) {
      console.error('Error fetching schedule:', err);
      console.error('Error response:', err.response?.data);

      if (err.response?.status === 401) {
        setError('Authentication failed. Your session may have expired. Please log out and log in again.');
      } else if (err.response?.status === 404) {
        setError('Schedule API endpoint not found. Please check your backend configuration.');
      } else if (err.response?.data?.error) {
        setError(`Server error: ${err.response.data.error}`);
      } else if (err.message === 'Network Error') {
        setError(`Network error. Please check if the backend server is running on ${API_BASE_URL}`);
      } else {
        setError(`Failed to load schedule data: ${err.message}`);
      }
      setScheduleData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Authentication token is missing. Please log in again.');
      return;
    }
    fetchScheduleData(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate, token, departmentId, locationId]);

  useEffect(() => {
    if (showMonthPicker) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMonthPicker]);

  const getFilteredData = () => {
    return scheduleData.filter(item => selectedFilters.has(item.type));
  };

  const getSchedulesForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return getFilteredData().filter(item => scheduleDateKey(item.date) === dateStr);
  };

  const getTypeColor = (type) => TYPE_DOT_COLOR[type] || '#6b7280';

  const goToMonth = (year, month) => {
    setCurrentDate(new Date(year, month, 1));
    if (!isCompact) return;
    const today = new Date();
    if (today.getFullYear() === year && today.getMonth() === month) {
      setSelectedDate(startOfDay(today));
    } else {
      setSelectedDate(new Date(year, month, 1));
    }
  };

  const handlePrevMonth = () => {
    goToMonth(currentDate.getFullYear(), currentDate.getMonth() - 1);
  };

  const handleNextMonth = () => {
    goToMonth(currentDate.getFullYear(), currentDate.getMonth() + 1);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    if (isCompact) {
      setSelectedDate(startOfDay(today));
    }
  };

  const generateCalendarDays = (padTrailing = false) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    if (padTrailing) {
      while (days.length % 7 !== 0) {
        days.push(null);
      }
    }

    return days;
  };

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const filteredData = getFilteredData();
  const typeCounts = {
    ppm: scheduleData.filter(item => item.type === 'ppm').length,
    calibration: scheduleData.filter(item => item.type === 'calibration').length,
    compliance: scheduleData.filter(item => item.type === 'compliance').length,
  };

  const openDevice = (schedule) => {
    if (onViewDevice && schedule.device_id) {
      onViewDevice(schedule.device_id);
    } else if (onNavigateToDevices && schedule.device_id) {
      onNavigateToDevices(schedule.device_id);
    }
  };

  if (isCompact) {
    const calendarDays = generateCalendarDays(true);
    const today = new Date();
    const isViewingCurrentMonth =
      currentDate.getFullYear() === today.getFullYear()
      && currentDate.getMonth() === today.getMonth();
    const monthTitle = isViewingCurrentMonth ? 'This month' : monthName;
    const selectedSchedules = selectedDate ? getSchedulesForDate(selectedDate) : [];
    const selectedLabel = selectedDate
      ? selectedDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      : '';

    return (
      <div className="calendar-view-container is-compact">
        <div className="calendar-compact-toolbar">
          <div className="calendar-compact-nav">
            <button type="button" className="calendar-compact-nav-btn" onClick={handlePrevMonth} title="Previous month">
              ←
            </button>
            <span className="calendar-compact-month">{monthTitle}</span>
            <button type="button" className="calendar-compact-nav-btn" onClick={handleNextMonth} title="Next month">
              →
            </button>
            <button type="button" className="calendar-compact-today" onClick={handleToday} title="Go to today">
              Today
            </button>
          </div>
          <div className="calendar-compact-chips">
            {FILTER_TYPES.map(({ type, label }) => (
              <button
                key={type}
                type="button"
                className={`calendar-chip${selectedFilters.has(type) ? ' is-active' : ''}`}
                data-type={type}
                onClick={() => toggleFilter(type)}
                aria-pressed={selectedFilters.has(type)}
              >
                {label} {typeCounts[type]}
              </button>
            ))}
            <span className="calendar-compact-total">{filteredData.length} in view</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <div className="error-content">
              <span>{error}</span>
            </div>
          </div>
        )}

        {loading && (
          <div className="calendar-compact-loading">Loading schedule…</div>
        )}

        {!loading && !error && (
          <div className="calendar-compact-layout">
            <div className="calendar-compact-grid" role="grid" aria-label={`${monthTitle} maintenance schedule`}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="calendar-compact-weekday" role="columnheader">
                  {day}
                </div>
              ))}
              {calendarDays.map((date, index) => {
                const schedules = date ? getSchedulesForDate(date) : [];
                const isToday = date && date.toDateString() === new Date().toDateString();
                const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
                const visibleDots = schedules.slice(0, 3);
                const overflow = schedules.length - visibleDots.length;

                return (
                  <button
                    key={index}
                    type="button"
                    disabled={!date}
                    className={[
                      'calendar-compact-day',
                      !date ? 'is-empty' : '',
                      isToday ? 'is-today' : '',
                      isSelected ? 'is-selected' : '',
                      schedules.length > 0 ? 'has-items' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => date && setSelectedDate(date)}
                    aria-label={date ? `${date.getDate()} ${monthName}${schedules.length ? `, ${schedules.length} due` : ''}` : undefined}
                    aria-pressed={isSelected}
                  >
                    {date && (
                      <>
                        <span className="calendar-compact-day-number">{date.getDate()}</span>
                        {schedules.length > 0 && (
                          <span className="calendar-compact-dots">
                            {visibleDots.map((item, dotIdx) => (
                              <span
                                key={`${item.device_id || item.device_identifier}-${dotIdx}`}
                                className="calendar-compact-dot"
                                style={{ background: getTypeColor(item.type) }}
                                title={`${getTypeLabel(item.type)} · ${item.device_name || item.device_identifier}`}
                              />
                            ))}
                            {overflow > 0 && (
                              <span className="calendar-compact-overflow">+{overflow}</span>
                            )}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="calendar-compact-pane-slot">
            <div className="calendar-compact-pane">
              <div className="calendar-compact-pane-title">
                {selectedLabel}
                {selectedSchedules.length > 0 ? ` · ${selectedSchedules.length}` : ''}
              </div>
              {selectedSchedules.length > 0 ? (
                <div className="calendar-compact-list">
                  {selectedSchedules.map((schedule, idx) => {
                    const canOpen = Boolean(schedule.device_id && (onViewDevice || onNavigateToDevices));
                    return (
                      <div
                        key={`${schedule.device_id || schedule.device_identifier}-${schedule.type}-${idx}`}
                        className={`calendar-compact-item${canOpen ? ' is-clickable' : ''}`}
                        data-type={schedule.type}
                        onClick={() => canOpen && openDevice(schedule)}
                        role={canOpen ? 'button' : undefined}
                        tabIndex={canOpen ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!canOpen) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openDevice(schedule);
                          }
                        }}
                      >
                        <span className="maintenance-type-badge" data-type={schedule.type}>
                          {getTypeLabel(schedule.type)}
                        </span>
                        <div className="calendar-compact-item-info">
                          <span className="calendar-compact-item-name">
                            {schedule.device_name || 'Unknown Device'}
                          </span>
                          <DeviceIDBadge deviceId={schedule.device_identifier || 'N/A'} size="small" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="calendar-compact-empty">No dues this day</p>
              )}
            </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const calendarDays = generateCalendarDays(false);

  return (
    <div className="calendar-view-container">
      {/* Calendar Header */}
      <div className="calendar-header">
        <h3 className="calendar-title">📅 Schedule Calendar</h3>
        <p className="calendar-subtitle">View preventive maintenance, calibration, and compliance schedules</p>
      </div>

      {/* Month Navigation with Inline Scrollable Selector */}
      <div className="month-navigation">
        <button className="nav-btn" onClick={handlePrevMonth} title="Previous month">
          ← Prev
        </button>
        
        {/* Inline Date Dropdowns */}
        <div className="inline-date-selectors">
          {/* Year Dropdown */}
          <div className="date-dropdown-wrapper">
            <select 
              className="date-dropdown"
              value={currentDate.getFullYear()}
              onChange={(e) => {
                const year = parseInt(e.target.value);
                setCurrentDate(new Date(year, currentDate.getMonth(), 1));
              }}
              title="Select year"
            >
              {Array.from({ length: 101 }, (_, i) => 2000 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Month Dropdown */}
          <div className="date-dropdown-wrapper">
            <select 
              className="date-dropdown"
              value={currentDate.getMonth()}
              onChange={(e) => {
                const month = parseInt(e.target.value);
                setCurrentDate(new Date(currentDate.getFullYear(), month, 1));
              }}
              title="Select month"
            >
              <option value="0">January</option>
              <option value="1">February</option>
              <option value="2">March</option>
              <option value="3">April</option>
              <option value="4">May</option>
              <option value="5">June</option>
              <option value="6">July</option>
              <option value="7">August</option>
              <option value="8">September</option>
              <option value="9">October</option>
              <option value="10">November</option>
              <option value="11">December</option>
            </select>
          </div>
        </div>

        <button className="nav-btn" onClick={handleNextMonth} title="Next month">
          Next →
        </button>
        <button className="today-btn" onClick={handleToday} title="Go to today">
          Today
        </button>
      </div>

      {/* Filter Checkboxes with Counts */}
      {!loading && !error && (
        <div className="filter-section">
          <div className="filter-checkboxes">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={selectedFilters.has('ppm')}
                onChange={() => toggleFilter('ppm')}
              />
              <span className="checkbox-icon">🔧</span>
              <div className="checkbox-content">
                <span className="checkbox-text">Preventive Maintenance (PPM)</span>
                <span className="checkbox-count">{typeCounts.ppm}</span>
              </div>
            </label>
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={selectedFilters.has('calibration')}
                onChange={() => toggleFilter('calibration')}
              />
              <span className="checkbox-icon">⚙️</span>
              <div className="checkbox-content">
                <span className="checkbox-text">Calibration (CAL)</span>
                <span className="checkbox-count">{typeCounts.calibration}</span>
              </div>
            </label>
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={selectedFilters.has('compliance')}
                onChange={() => toggleFilter('compliance')}
              />
              <span className="checkbox-icon">✅</span>
              <div className="checkbox-content">
                <span className="checkbox-text">Compliance (COM)</span>
                <span className="checkbox-count">{typeCounts.compliance}</span>
              </div>
            </label>
          </div>
          <div className="filter-total">
            <span className="total-label">Total:</span>
            <span className="total-count">{filteredData.length}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <div className="error-content">
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading schedule data...</p>
        </div>
      )}

      {/* Calendar Grid */}
      {!loading && !error && (
        <div className="calendar-grid-wrapper">
          {/* Weekday Headers */}
          <div className="calendar-grid">
            <div className="weekday-header">Sun</div>
            <div className="weekday-header">Mon</div>
            <div className="weekday-header">Tue</div>
            <div className="weekday-header">Wed</div>
            <div className="weekday-header">Thu</div>
            <div className="weekday-header">Fri</div>
            <div className="weekday-header">Sat</div>

            {/* Calendar Days */}
            {calendarDays.map((date, index) => {
              const schedules = date ? getSchedulesForDate(date) : [];
              const isToday = date && 
                date.toDateString() === new Date().toDateString();
              const isCurrentMonth = date && date.getMonth() === currentDate.getMonth();

              return (
                <div
                  key={index}
                  className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => date && setSelectedDateDetails(date)}
                >
                  {date && (
                    <>
                      <div className="day-number">{date.getDate()}</div>
                      <div className="day-schedules">
                        {schedules.length > 0 ? (
                          <div className="schedule-dots">
                            {(() => {
                              // Group schedules by type
                              const grouped = {
                                ppm: schedules.filter(s => s.type === 'ppm'),
                                calibration: schedules.filter(s => s.type === 'calibration'),
                                compliance: schedules.filter(s => s.type === 'compliance')
                              };
                              
                              return Object.entries(grouped).map(([type, items]) => {
                                if (items.length === 0) return null;
                                
                                let bgLight, bgDark, border;
                                if (type === 'ppm') {
                                  bgLight = '#bfdbfe';
                                  bgDark = '#93c5fd';
                                  border = '#60a5fa';
                                } else if (type === 'calibration') {
                                  bgLight = '#fef3c7';
                                  bgDark = '#fde68a';
                                  border = '#fbbf24';
                                } else if (type === 'compliance') {
                                  bgLight = '#d1fae5';
                                  bgDark = '#a7f3d0';
                                  border = '#6ee7b7';
                                }
                                
                                return (
                                  <div
                                    key={type}
                                    className="schedule-item-group"
                                    style={{
                                      '--schedule-bg-light': bgLight,
                                      '--schedule-bg-dark': bgDark,
                                      '--schedule-border': border
                                    }}
                                    title={`${items.length} ${getTypeLabel(type)} schedule(s)`}
                                  >
                                    <span className="schedule-icon">{getTypeIcon(type)}</span>
                                    <span style={{ flex: 1 }}>{getTypeLabel(type)}</span>
                                    {items.length > 1 && (
                                      <span className="schedule-count">{items.length}</span>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Date Details Modal */}
      {selectedDateDetails && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setSelectedDateDetails(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedDateDetails.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <ModalCloseButton onClick={() => setSelectedDateDetails(null)} title="Close" />
            </div>

            <div className="modal-body">
              {getSchedulesForDate(selectedDateDetails).length > 0 ? (
                <div className="modal-schedules">
                  {getSchedulesForDate(selectedDateDetails).map((schedule, idx) => (
                    <div key={idx} className="modal-schedule-item" style={{ borderLeftColor: getTypeColor(schedule.type) }}>
                      <div className="modal-schedule-icon">{getTypeIcon(schedule.type)}</div>
                      <div className="modal-schedule-info">
                        <h4>{schedule.device_name}</h4>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <DeviceIDBadge deviceId={schedule.device_identifier || 'N/A'} size="small" />
                        </div>
                        <p><strong>Type:</strong> {schedule.description}</p>
                      </div>
                      <button
                        className="modal-schedule-btn"
                        onClick={() => {
                          openDevice(schedule);
                          setSelectedDateDetails(null);
                        }}
                      >
                        View Device
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modal-empty">
                  <p>No schedules for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CalendarView;
